import type { FilterPayloadNode } from '@zoryn/components/grid'
import type { RuleCondition, RuleGroupState, SystemParamField } from './rules'
import type { GridFilterOperator } from '@zoryn/components/grid'
import { getRuleOperator, resolveRuleColumnType } from './rule-meta'
import { createRuleId } from './rule-group'

export function isEmptyRuleValue(val: unknown) {
  return val === null || val === undefined || val === ''
}

export function isActiveRule(cond: RuleCondition) {
  if (!cond.field) {
    return false
  }
  if (cond.operator === 'empty' || cond.operator === 'notEmpty') {
    return true
  }
  if (cond.type === 'switch') {
    return cond.value === true || cond.value === false
  }
  return !isEmptyRuleValue(cond.value)
}

export function isValuelessOperator(operator: GridFilterOperator) {
  return operator === 'empty' || operator === 'notEmpty'
}

function buildConditionPayload(cond: RuleCondition, index: number): FilterPayloadNode {
  const node: FilterPayloadNode = {
    field: cond.field,
    operator: cond.operator,
    value: cond.value,
  }
  if (cond.valueLabel) {
    node.valueLabel = cond.valueLabel
  }
  if (index > 0) {
    node.logic = cond.logic || 'and'
  }
  return node
}

function attachSiblingLogic(
  payload: FilterPayloadNode,
  index: number,
  siblingLogic?: string
): FilterPayloadNode {
  if (index === 0) {
    return payload
  }
  return {
    ...payload,
    logic: siblingLogic || 'or',
  }
}

function buildGroupPayload(group: RuleGroupState): FilterPayloadNode | null {
  const activeConditions = group.conditions.filter(isActiveRule)
  if (!activeConditions.length) {
    return null
  }
  if (activeConditions.length === 1) {
    return buildConditionPayload(activeConditions[0], 0)
  }
  return {
    type: 'group',
    children: activeConditions.map((cond, index) => buildConditionPayload(cond, index)),
  }
}

export function buildRulePayload(
  ruleGroups: RuleGroupState[]
): FilterPayloadNode | FilterPayloadNode[] | null {
  const groupPayloads = ruleGroups
    .map(buildGroupPayload)
    .filter((item): item is FilterPayloadNode => item != null)

  if (!groupPayloads.length) {
    return null
  }

  if (groupPayloads.length === 1) {
    const only = groupPayloads[0]
    if (only.type === 'group' && only.children?.length) {
      return only.children
    }
    return only
  }

  const rootLogic = ruleGroups[1]?.groupLogic || 'or'
  return {
    type: 'group',
    children: groupPayloads.map((payload, index) =>
      attachSiblingLogic(payload, index, ruleGroups[index]?.groupLogic || rootLogic)
    ),
  }
}

export function getFirstRuleField(fields: Array<{ field: string }>) {
  return fields.find((item) => Boolean(item.field))
}

export function isSameRulePayload(
  left: FilterPayloadNode | FilterPayloadNode[] | null,
  right: FilterPayloadNode | FilterPayloadNode[] | null
) {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
}

function defaultOperatorForType(type: RuleCondition['type']): GridFilterOperator {
  if (type === 'number' || type === 'date' || type === 'switch') {
    return 'eq'
  }
  return 'like'
}

function parseConditionNode(
  node: FilterPayloadNode,
  index: number,
  fieldList: SystemParamField[]
): RuleCondition {
  const param = fieldList.find((item) => item.field === node.field)
  const type = param ? resolveRuleColumnType(param.columnType) : 'input'
  const operator = node.operator ?? defaultOperatorForType(type)
  return {
    id: createRuleId('cond'),
    field: node.field ?? '',
    operator,
    type,
    value: node.value ?? (type === 'switch' ? false : null),
    operatorList: getRuleOperator(type),
    logic: index === 0 ? 'and' : node.logic || 'and',
    dataSource: param?.dataSource,
    valueLabel:
      node.valueLabel != null && node.valueLabel !== ''
        ? String(node.valueLabel)
        : undefined,
  }
}

function createGroupFromConditions(
  conditions: RuleCondition[],
  groupLogic: RuleGroupState['groupLogic'] = 'or'
): RuleGroupState {
  return {
    id: createRuleId('group'),
    logic: 'and',
    groupLogic,
    conditions,
  }
}

function parseSingleGroupPayload(
  payload: FilterPayloadNode | FilterPayloadNode[],
  fieldList: SystemParamField[]
): RuleGroupState {
  const nodes = Array.isArray(payload) ? payload : [payload]
  const conditions = nodes.map((node, index) => parseConditionNode(node, index, fieldList))
  return createGroupFromConditions(conditions)
}

function parseMultiGroupPayload(
  root: FilterPayloadNode,
  fieldList: SystemParamField[]
): RuleGroupState[] {
  const children = root.children ?? []
  return children.map((child, index) => {
    const groupLogic = (index === 0 ? 'or' : child.logic ?? 'or') as RuleGroupState['groupLogic']
    if (child.type === 'group' && child.children?.length) {
      const conditions = child.children.map((node, condIndex) =>
        parseConditionNode(node, condIndex, fieldList)
      )
      return createGroupFromConditions(conditions, groupLogic)
    }
    return createGroupFromConditions(
      [parseConditionNode(child, 0, fieldList)],
      groupLogic
    )
  })
}

/** buildRulePayload 的逆操作：将 rulesJson 还原为条件组 UI 状态 */
export function parseRulePayload(
  payload: FilterPayloadNode | FilterPayloadNode[] | null,
  fieldList: SystemParamField[] = []
): RuleGroupState[] {
  if (payload == null) {
    return []
  }
  if (Array.isArray(payload)) {
    if (!payload.length) {
      return []
    }
    return [parseSingleGroupPayload(payload, fieldList)]
  }
  if (payload.type === 'group' && payload.children?.length) {
    return parseMultiGroupPayload(payload, fieldList)
  }
  return [parseSingleGroupPayload(payload, fieldList)]
}
